import {
  createDictionary as createDictionaryEndpoint,
  createDictionaryEntry as createDictionaryEntryEndpoint,
  deleteDictionary as deleteDictionaryEndpoint,
  deleteDictionaryEntry as deleteDictionaryEntryEndpoint,
  getDictionary as getDictionaryEndpoint,
  listDictionaries as listDictionariesEndpoint,
  updateDictionary as updateDictionaryEndpoint,
  updateDictionaryEntry as updateDictionaryEntryEndpoint,
  type ApiClient,
  type CreateDictionaryRequest,
  type UpsertDictionaryEntryRequest,
} from '@kiao-voice/api-client'
import { createWebApiClient, getWebApiClient } from '../../lib/web-api-client'
import type { DictionaryDetail, DictionaryEntryView, DictionarySummary } from './types'

/**
 * Web bindings over the shared `@kiao-voice/api-client` dictionary endpoints —
 * the same functions desktop compiles against, so the route/shape contract has
 * exactly one copy. Bearer token + `X-Active-Context` are injected by the
 * shared web client from storage (#223); `fetchFn` is a test seam.
 *
 * Dictionaries are a per-USER resource, but the routes sit inside the
 * org-context middleware group, so a revoked/stale active context can still
 * 403 (`active_context_forbidden`) before the controller runs; the shared
 * client's self-heal covers reads, and a write surfaces the generic failure
 * message. Non-2xx responses throw the shared client's `ApiError` as-is. A 422
 * body carries a LOCALIZED human message only (`{ error: '…' }` — no machine
 * code), so callers branch on `error.status` and never on the message text;
 * ambiguous same-status cases are disambiguated client-side (duplicate
 * prechecks against loaded entries).
 */
interface RequestOptions {
  fetchFn?: typeof fetch
}

function client(opts?: RequestOptions): ApiClient {
  return opts?.fetchFn ? createWebApiClient(opts.fetchFn) : getWebApiClient()
}

export type DictionaryInput = CreateDictionaryRequest
export type DictionaryEntryInput = UpsertDictionaryEntryRequest

/** List the caller's dictionaries, most-recently-updated first (server order). */
export async function listDictionaries(opts: RequestOptions = {}): Promise<DictionarySummary[]> {
  return listDictionariesEndpoint(client(opts))
}

/** Fetch one dictionary with its full entry list, ordered by `sortOrder` then `id`. */
export async function getDictionary(
  id: number,
  opts: RequestOptions = {}
): Promise<DictionaryDetail> {
  return getDictionaryEndpoint(client(opts), id)
}

/** Create a dictionary. 422 past the 50/user cap. */
export async function createDictionary(
  data: DictionaryInput,
  opts: RequestOptions = {}
): Promise<DictionarySummary> {
  return createDictionaryEndpoint(client(opts), data)
}

/** Rename/re-describe a dictionary. */
export async function updateDictionary(
  id: number,
  data: DictionaryInput,
  opts: RequestOptions = {}
): Promise<DictionarySummary> {
  return updateDictionaryEndpoint(client(opts), id, data)
}

/** Soft-delete a dictionary. Resolves with no value on 204. */
export async function deleteDictionary(id: number, opts: RequestOptions = {}): Promise<void> {
  await deleteDictionaryEndpoint(client(opts), id)
}

/** Add a term to a dictionary. 422 past the 300/dictionary cap or on a repeated
 *  term (indistinguishable by status — duplicates are precaught client-side and
 *  treated as benign, see `docs/dictionary-batch-transcription-plan.md` §5.3). */
export async function createDictionaryEntry(
  dictionaryId: number,
  data: DictionaryEntryInput,
  opts: RequestOptions = {}
): Promise<DictionaryEntryView> {
  return createDictionaryEntryEndpoint(client(opts), dictionaryId, data)
}

/** Edit a term's text/sort order. */
export async function updateDictionaryEntry(
  dictionaryId: number,
  entryId: number,
  data: DictionaryEntryInput,
  opts: RequestOptions = {}
): Promise<DictionaryEntryView> {
  return updateDictionaryEntryEndpoint(client(opts), dictionaryId, entryId, data)
}

/** Delete a term. Resolves with no value on 204. */
export async function deleteDictionaryEntry(
  dictionaryId: number,
  entryId: number,
  opts: RequestOptions = {}
): Promise<void> {
  await deleteDictionaryEntryEndpoint(client(opts), dictionaryId, entryId)
}
